import React, { Suspense, useState, useCallback, useRef, useEffect } from 'react';
import GridRenderer from './components/GridRenderer';
import ExportPanel from './components/ExportPanel';
import VocabularyPicker from './components/VocabularyPicker';
import AncientPoemPicker from './components/AncientPoemPicker';
import { convertToPinyin } from './utils/pinyinConverter';

const LiteracyPicker = React.lazy(() => import('./components/LiteracyPicker'));

const DEFAULT_SETTINGS = {
  // 页面设置
  marginTop: 40,
  marginBottom: 60,
  marginLeft: 40,
  marginRight: 40,

  // 格子设置
  cellSize: 40,
  pinyinHeight: 20,
  colsPerRow: 12,
  rowGap: 0,
  gridStyle: 'tianzige',
  gridColor: '#ff0000',

  // 文字设置
  hanziColor: '#333333',
  showPinyin: true,
  pinyinColor: '#333333',
  practiceMode: 'normal',

  // 格线设置
  showPinyinLines: true,
  showPinyinBorder: true,
  showGridLines: true,
  showGridBorder: true,

  // 打印设置
  showPageNumbers: false,
};

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const STORAGE_KEY = 'cygg-copybook-preferences';

function loadSavedPreferences() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      settings: { ...DEFAULT_SETTINGS, ...(saved.settings || {}) },
      pageMode: saved.pageMode === 'poem' ? 'poem' : 'hanzi',
      showPoemTitle: Boolean(saved.showPoemTitle),
      previewScale: saved.previewScale || 'auto',
    };
  } catch {
    return {
      settings: DEFAULT_SETTINGS,
      pageMode: 'hanzi',
      showPoemTitle: false,
      previewScale: 'auto',
    };
  }
}

function buildVocabularyTitle(meta) {
  if (!meta) return '';
  const prefix = `${meta.gradeLabel || ''}${meta.semester || ''}`;
  return meta.all
    ? `${prefix}词语默写`
    : `${prefix}第${meta.lessonId}课词语默写`;
}

function buildLiteracyTitle(meta) {
  if (!meta) return '';
  const prefix = `${meta.gradeLabel || ''}${meta.semester || ''}`;
  const section = meta.sectionTitle || '识字表';
  return meta.all
    ? `${prefix}${section}`
    : `${prefix}${section}${meta.lessonTitle || ''}`;
}

function buildPoemTitle(poems) {
  const list = Array.isArray(poems) ? poems : [poems].filter(Boolean);
  if (list.length === 1) {
    return `《${list[0].title || '古诗'}》默写`;
  }
  return list.length > 1 ? '古诗词默写' : '';
}

function estimateTextUnits(inputText) {
  return Array.from(inputText || '').reduce((total, char) => {
    if (char === '\n' || char === '\r') return total;
    if (char === ' ' || char === '　') return total + 0.35;
    return /[一-鿿]/.test(char) ? total + 1 : total + 0.8;
  }, 0);
}

function calculateAutoLayout(inputText, currentSettings) {
  const units = estimateTextUnits(inputText);
  const marginLeft = 36;
  const marginRight = 36;
  const marginTop = 36;
  const marginBottom = 44;
  const usableWidth = PAGE_WIDTH - marginLeft - marginRight;
  const usableHeight = PAGE_HEIGHT - marginTop - marginBottom;
  const candidates = (!currentSettings.showPinyin || currentSettings.practiceMode === 'copy')
    ? [64, 58, 52, 48, 44, 40, 36]
    : [52, 48, 44, 40, 36, 32];

  let best = null;

  candidates.forEach((cellSize) => {
    const pinyinHeight = currentSettings.showPinyin ? Math.round(cellSize * 0.54) : currentSettings.pinyinHeight;
    const colsPerRow = Math.max(4, Math.min(16, Math.floor(usableWidth / cellSize)));
    const rowGap = currentSettings.practiceMode === 'copy' ? Math.max(6, Math.round(cellSize * 0.12)) : (cellSize >= 44 ? 4 : 2);
    const rowHeight = currentSettings.showPinyin ? cellSize + pinyinHeight : cellSize;
    const rows = Math.max(1, Math.ceil(units / colsPerRow));
    const rowsPerPage = Math.max(1, Math.floor((usableHeight + rowGap) / (rowHeight + rowGap)));
    const pages = Math.ceil(rows / rowsPerPage);
    const lastPageRows = rows % rowsPerPage || rowsPerPage;
    const lastPageFill = lastPageRows / rowsPerPage;
    const balancePenalty = pages > 1 ? Math.abs(lastPageFill - 0.78) * 120 : Math.abs(lastPageFill - 0.55) * 80;
    const score = pages * 1000 + balancePenalty - cellSize * 4;

    if (!best || score < best.score) {
      best = {
        score,
        cellSize,
        pinyinHeight,
        colsPerRow,
        rowGap,
        marginTop,
        marginBottom,
        marginLeft,
        marginRight,
      };
    }
  });

  return best || {};
}

function calculatePoemLayout(currentSettings) {
  const colsPerRow = currentSettings.colsPerRow || 12;
  const marginTop = 58;
  const marginBottom = 58;
  const targetCellSize = colsPerRow <= 5 ? 72 : colsPerRow <= 7 ? 64 : colsPerRow <= 10 ? 52 : 44;
  const cellSize = Math.min(targetCellSize, Math.floor((PAGE_WIDTH - 72) / colsPerRow));
  const marginLeft = Math.floor((PAGE_WIDTH - colsPerRow * cellSize) / 2);
  const marginRight = marginLeft;
  const pinyinHeight = currentSettings.showPinyin ? Math.round(cellSize * 0.54) : currentSettings.pinyinHeight;

  return {
    colsPerRow,
    cellSize,
    pinyinHeight,
    rowGap: 12,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
  };
}

function inferPoemType(inputText) {
  const lines = (inputText || '')
    .split(/\r?\n/)
    .map(line => line.replace(/[，。！？、,.!?；;：:\s]/g, ''))
    .filter(Boolean);
  const maxLength = Math.max(0, ...lines.map(line => Array.from(line).length));
  return maxLength <= 5 ? '五言' : '七言';
}

function cleanPoemLine(line) {
  return String(line || '').replace(/[，。！？、,.!?；;：:\s/]/g, '');
}

function getPoemType(poem) {
  if (poem?.type) return poem.type;
  if (poem?.genre?.includes('五言')) return '五言';
  if (poem?.genre?.includes('七言')) return '七言';
  return inferPoemType((poem?.lines || []).join('\n'));
}

function formatPoemText(lines, colsPerRow) {
  const rows = [];
  let currentRow = '';
  let currentUnits = 0;

  (lines || []).forEach((line) => {
    const cleanLine = cleanPoemLine(line);
    if (!cleanLine) {
      if (currentRow) {
        rows.push(currentRow);
        currentRow = '';
        currentUnits = 0;
      }
      rows.push('');
      return;
    }

    const lineUnits = Array.from(cleanLine).length;
    const separatorUnits = 0;

    if (currentRow && currentUnits + separatorUnits + lineUnits > colsPerRow) {
      rows.push(currentRow);
      currentRow = cleanLine;
      currentUnits = lineUnits;
    } else {
      currentRow = currentRow ? `${currentRow}${cleanLine}` : cleanLine;
      currentUnits += separatorUnits + lineUnits;
    }
  });

  if (currentRow) {
    rows.push(currentRow);
  }

  return rows.join('\n');
}

function buildPoemLines(poems) {
  return (poems || []).flatMap((poem, index) => {
    const lines = poem?.lines || [];
    return index === 0 ? lines : ['', ...lines];
  });
}

function formatPoemHeader(poem) {
  const dynastyAuthor = [poem?.dynasty, poem?.author].filter(Boolean).join('·');
  return `《${poem?.title || ''}》${dynastyAuthor ? ` ${dynastyAuthor}` : ''}`;
}

function buildPoemWorksheet(poems, colsPerRow) {
  const rows = [];
  const titleRows = [];

  (poems || []).forEach((poem, index) => {
    if (index > 0) {
      rows.push('');
    }

    titleRows.push({
      lineIndex: rows.length,
      title: formatPoemHeader(poem),
    });

    const poemRows = formatPoemText(poem?.lines || [], colsPerRow).split('\n');
    rows.push(...poemRows);
  });

  return {
    text: rows.join('\n'),
    rawLines: buildPoemLines(poems),
    titleRows,
  };
}

function App() {
  const savedPreferences = useRef(loadSavedPreferences());
  const [text, setText] = useState('');
  const [characters, setCharacters] = useState([]);
  const [settings, setSettings] = useState(savedPreferences.current.settings);
  const [pageMode, setPageMode] = useState(savedPreferences.current.pageMode);
  const [showVocabPicker, setShowVocabPicker] = useState(false);
  const [showPoemPicker, setShowPoemPicker] = useState(false);
  const [showLiteracyPicker, setShowLiteracyPicker] = useState(false);
  const [currentPoemLines, setCurrentPoemLines] = useState(null);
  const [currentPoems, setCurrentPoems] = useState([]);
  const [currentPoemTitleRows, setCurrentPoemTitleRows] = useState([]);
  const [showPoemTitle, setShowPoemTitle] = useState(savedPreferences.current.showPoemTitle);
  const [customTitle, setCustomTitle] = useState('');
  const [previewScale, setPreviewScale] = useState(savedPreferences.current.previewScale);
  const [autoPreviewScale, setAutoPreviewScale] = useState(1);
  const gridRef = useRef(null);
  const previewAreaRef = useRef(null);

  // 处理文本变化
  const handleTextChange = useCallback((newText) => {
    setText(newText);
    if (pageMode === 'poem') {
      setCurrentPoemLines(null);
      setCurrentPoems([]);
      setCurrentPoemTitleRows([]);
    }
  }, [pageMode]);

  useEffect(() => {
    if (!text.trim()) {
      setCharacters([]);
      return;
    }

    const results = convertToPinyin(text, {
      toneType: 'symbol',
      multiple: true
    });

    setCharacters(results);
  }, [text]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      settings,
      pageMode,
      showPoemTitle,
      previewScale,
    }));
  }, [settings, pageMode, showPoemTitle, previewScale]);

  useEffect(() => {
    if (previewScale !== 'auto') return undefined;
    const updateScale = () => {
      const width = previewAreaRef.current?.clientWidth || PAGE_WIDTH;
      const nextScale = Math.min(1, Math.max(0.45, (width - 48) / PAGE_WIDTH));
      setAutoPreviewScale(Number(nextScale.toFixed(2)));
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    if (previewAreaRef.current) {
      observer.observe(previewAreaRef.current);
    }
    window.addEventListener('resize', updateScale);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, [previewScale]);

  const handleSettingsChange = useCallback((key, value) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      if (pageMode === 'poem' && key === 'colsPerRow') {
        return {
          ...next,
          ...calculatePoemLayout(next),
        };
      }
      return next;
    });
    if (pageMode === 'poem' && key === 'colsPerRow' && currentPoemLines?.length) {
      if (currentPoems.length > 0) {
        const worksheet = buildPoemWorksheet(currentPoems, value);
        setText(worksheet.text);
        setCurrentPoemLines(worksheet.rawLines);
        setCurrentPoemTitleRows(worksheet.titleRows);
      } else {
        setText(formatPoemText(currentPoemLines, value));
      }
    }
  }, [pageMode, currentPoemLines, currentPoems]);

  const handleReset = useCallback(() => {
    setText('');
    setCharacters([]);
    setCurrentPoemLines(null);
    setCurrentPoems([]);
    setCurrentPoemTitleRows([]);
    setCustomTitle('');
    setPreviewScale('auto');
    setSettings(DEFAULT_SETTINGS);
  }, []);

  const handleAutoLayout = useCallback((targetText = text) => {
    if (!targetText.trim()) return;
    setSettings(prev => ({
      ...prev,
      ...(pageMode === 'poem'
        ? calculatePoemLayout(prev)
        : calculateAutoLayout(targetText, prev)),
    }));
  }, [text, pageMode]);

  const handlePinyinSelect = useCallback((charIndex, pinyinIndex) => {
    setCharacters(prev => {
      const newCharacters = [...prev];
      const char = { ...newCharacters[charIndex] };
      char.selectedPinyinIndex = pinyinIndex;
      char.pinyin = char.allPinyins[pinyinIndex];
      newCharacters[charIndex] = char;
      return newCharacters;
    });
  }, []);

  const stats = {
    hanziCount: characters.filter(c => c.isHanzi).length,
    lines: text.trim() ? text.split(/\r?\n/).length : 0
  };

  const hasPreview = characters.some(char => char.isHanzi);
  const poemTitleRows = showPoemTitle ? currentPoemTitleRows : [];
  const worksheetTitle = customTitle.trim();
  const pageTitle = pageMode === 'poem' ? '古诗字帖' : '汉字字帖';
  const actualPreviewScale = previewScale === 'auto' ? autoPreviewScale : Number(previewScale);

  return (
    <div className="app-container">
      <nav className="top-nav">
        <div className="top-nav-left">
          <span style={{ fontWeight: '600', color: '#333' }}>此意关关·字帖</span>
          <div className="nav-tabs">
            <button
              className={`nav-tab ${pageMode === 'hanzi' ? 'active' : ''}`}
              onClick={() => {
                setPageMode('hanzi');
                setText('');
                setCharacters([]);
                setCurrentPoemLines(null);
                setCurrentPoems([]);
                setCurrentPoemTitleRows([]);
                setCustomTitle('');
                setSettings(DEFAULT_SETTINGS);
              }}
            >
              汉字字帖
            </button>
            <button
              className={`nav-tab ${pageMode === 'poem' ? 'active' : ''}`}
              onClick={() => {
                setPageMode('poem');
                setText('');
                setCharacters([]);
                setCurrentPoemLines(null);
                setCurrentPoems([]);
                setCurrentPoemTitleRows([]);
                setCustomTitle('');
                setSettings(prev => ({
                  ...prev,
                  ...calculatePoemLayout(prev),
                }));
              }}
            >
              古诗字帖
            </button>
            <a
              className="nav-link"
              href="https://poetry.cygg.top"
              target="_blank"
              rel="noopener noreferrer"
            >
              推荐网站：诗境
            </a>
          </div>
        </div>
        <div className="top-nav-right">
          <button className="nav-btn" onClick={handleReset}>重置</button>
          <ExportPanel gridRef={gridRef} characterCount={hasPreview ? 1 : 0} pageMode={pageMode} customTitle={customTitle} />
        </div>
      </nav>

      <div className="main-content">
        <aside className="left-panel">
          <div className="panel-section">
            <div className="panel-section-title">格子样式</div>
            <div className="grid-style-selector">
              {[
                { id: 'tianzige', name: '田字格', icon: '⊞' },
                { id: 'mizige', name: '米字格', icon: '✳' },
                { id: 'fangge', name: '方格', icon: '□' },

              ].map(style => (
                <button
                  key={style.id}
                  className={`grid-style-btn ${settings.gridStyle === style.id ? 'active' : ''}`}
                  onClick={() => handleSettingsChange('gridStyle', style.id)}
                  title={style.name}
                >
                  <span style={{ fontSize: '20px' }}>{style.icon}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="panel-section">
            <div className="panel-section-title">页面布局</div>
            <button
              className="nav-btn"
              style={{ width: '100%' }}
              disabled={!hasPreview}
              onClick={() => handleAutoLayout()}
            >
              自动布局
            </button>
          </div>

          <div className="panel-section">
            <div className="slider-control">
              <div className="slider-header">
                <span className="slider-label">每行格数</span>
                <span className="slider-value">{settings.colsPerRow}</span>
              </div>
              <input
                type="range"
                min="4"
                max="14"
                value={settings.colsPerRow}
                onChange={(e) => handleSettingsChange('colsPerRow', parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="panel-section">
            <div className="slider-control">
              <div className="slider-header">
                <span className="slider-label">格子大小</span>
                <span className="slider-value">{settings.cellSize}</span>
              </div>
              <input
                type="range"
                min="32"
                max="72"
                value={settings.cellSize}
                onChange={(e) => handleSettingsChange('cellSize', parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="panel-section">
            <div className="slider-control">
              <div className="slider-header">
                <span className="slider-label">行间距</span>
                <span className="slider-value">{settings.rowGap}</span>
              </div>
              <input
                type="range"
                min="0"
                max="40"
                value={settings.rowGap}
                onChange={(e) => handleSettingsChange('rowGap', parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="panel-section">
            <div className="panel-item">
              <span className="panel-item-label">边框颜色</span>
              <div className="color-picker">
                <input
                  type="color"
                  value={settings.gridColor}
                  onChange={(e) => handleSettingsChange('gridColor', e.target.value)}
                  style={{ width: '24px', height: '24px', border: 'none', cursor: 'pointer' }}
                />
                <span className="color-value">{settings.gridColor}</span>
              </div>
            </div>
          </div>

          <div className="panel-section">
            <div className="panel-section-title">格线设置</div>
            <div className="property-item" onClick={() => handleSettingsChange('showPinyinLines', !settings.showPinyinLines)}>
              <span className="property-label">拼音四线三格</span>
              <div className={`toggle-switch ${settings.showPinyinLines ? 'active' : ''}`} />
            </div>
            <div className="property-item" onClick={() => handleSettingsChange('showPinyinBorder', !settings.showPinyinBorder)}>
              <span className="property-label">拼音外框</span>
              <div className={`toggle-switch ${settings.showPinyinBorder ? 'active' : ''}`} />
            </div>
            <div className="property-item" onClick={() => handleSettingsChange('showGridLines', !settings.showGridLines)}>
              <span className="property-label">田字格线条</span>
              <div className={`toggle-switch ${settings.showGridLines ? 'active' : ''}`} />
            </div>
            <div className="property-item" onClick={() => handleSettingsChange('showGridBorder', !settings.showGridBorder)}>
              <span className="property-label">田字格外框</span>
              <div className={`toggle-switch ${settings.showGridBorder ? 'active' : ''}`} />
            </div>
          </div>

          <a
            className="icp-card"
            href="https://beian.miit.gov.cn"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="icp-dot" />
            <span>陕ICP备2026015575号</span>
          </a>
        </aside>

        <main className="preview-area" ref={previewAreaRef}>
          <div className="preview-toolbar">
            <span>预览缩放</span>
            <select value={previewScale} onChange={(event) => setPreviewScale(event.target.value)}>
              <option value="auto">适应宽度</option>
              <option value="1">100%</option>
              <option value="0.75">75%</option>
              <option value="0.5">50%</option>
            </select>
          </div>
          <div
            className="paper-viewport"
            style={{
              width: `${PAGE_WIDTH * actualPreviewScale}px`,
            }}
          >
            <div
              className="paper-stack"
              ref={gridRef}
              style={{
                transform: `scale(${actualPreviewScale})`,
                transformOrigin: 'top center',
              }}
            >
              {hasPreview ? (
                <GridRenderer
                  characters={characters}
                  settings={settings}
                  titleRows={poemTitleRows}
                  worksheetTitle={worksheetTitle}
                  showPageNumbers={settings.showPageNumbers}
                  onPinyinSelect={handlePinyinSelect}
                />
              ) : (
                <div className="paper-preview">
                  <div className="empty-preview">
                    <strong>{pageTitle}</strong>
                    <span>
                      {pageMode === 'poem'
                        ? '在右侧导入或输入古诗，预览将显示在这里'
                        : '在右侧输入汉字，预览将显示在这里'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <aside className="right-panel">
          <div className="input-section">
            <h3>{pageMode === 'poem' ? '输入古诗' : '输入汉字'}</h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              {pageMode === 'poem' ? (
                <button
                  className="nav-btn"
                  style={{ flex: 1, fontSize: '12px' }}
                  onClick={() => setShowPoemPicker(true)}
                >
                  古诗词导入
                </button>
              ) : (
                <>
                  {pageMode === 'hanzi' && (
                    <button
                      className="nav-btn"
                      style={{ flex: 1, fontSize: '12px' }}
                      onClick={() => setShowVocabPicker(true)}
                    >
                      词语表导入
                    </button>
                  )}
                  <button
                    className="nav-btn"
                    style={{ flex: 1, fontSize: '12px' }}
                    onClick={() => setShowLiteracyPicker(true)}
                  >
                    识字表导入
                  </button>
                </>
              )}
            </div>
            <textarea
              className="input-textarea"
              value={text}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder={pageMode === 'poem' ? '请输入古诗，建议每句一行...' : '请输入汉字，支持回车换行...'}
              rows={4}
            />
            <div className="input-stats">
              <span>汉字: <b>{stats.hanziCount}</b></span>
              <span>行数: <b>{stats.lines}</b></span>
            </div>
            <div className="input-tip">
              提示：点击多音字可以切换拼音。
            </div>
          </div>

          <div className="panel-section">
            <div className="panel-section-title">标题</div>
            <input
              value={customTitle}
              onChange={(event) => setCustomTitle(event.target.value)}
              placeholder={pageMode === 'poem' ? '如：第一单元古诗默写' : '如：第一单元默写'}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                fontSize: '12px',
                outline: 'none',
              }}
            />
          </div>

          {pageMode === 'poem' && (
            <div className="panel-section">
              <div className="panel-section-title">古诗词</div>
              <div className="property-item" onClick={() => setShowPoemTitle(prev => !prev)}>
                <span className="property-label">显示诗题作者</span>
                <div className={`toggle-switch ${showPoemTitle ? 'active' : ''}`} />
              </div>
            </div>
          )}

          <div className="panel-section">
            <div className="panel-section-title">拼音</div>
            <div
              className="property-item"
              onClick={() => handleSettingsChange('showPinyin', !settings.showPinyin)}
            >
              <span className="property-label">显示拼音</span>
              <div className={`toggle-switch ${settings.showPinyin ? 'active' : ''}`} />
            </div>
            <div className="panel-item">
              <span className="panel-item-label">拼音颜色</span>
              <div className="color-picker">
                <input
                  type="color"
                  value={settings.pinyinColor}
                  onChange={(e) => handleSettingsChange('pinyinColor', e.target.value)}
                  style={{ width: '24px', height: '24px', border: 'none', cursor: 'pointer' }}
                />
                <span className="color-value">{settings.pinyinColor}</span>
              </div>
            </div>
          </div>

          <div className="panel-section">
            <div className="panel-section-title">文字</div>
            <div className="panel-item">
              <span className="panel-item-label">文字颜色</span>
              <div className="color-picker">
                <input
                  type="color"
                  value={settings.hanziColor}
                  onChange={(e) => handleSettingsChange('hanziColor', e.target.value)}
                  style={{ width: '24px', height: '24px', border: 'none', cursor: 'pointer' }}
                />
                <span className="color-value">{settings.hanziColor}</span>
              </div>
            </div>
            <div className="property-item" onClick={() => {
              const modes = ['normal', 'trace', 'copy'];
              const next = modes[(modes.indexOf(settings.practiceMode) + 1) % modes.length];
              handleSettingsChange('practiceMode', next);
            }}>
              <span className="property-label">文字显示</span>
              <span className="property-value">
                {settings.practiceMode === 'normal' ? '正常' : settings.practiceMode === 'trace' ? '描红' : '空白'}
                <span className="property-icon" />
              </span>
            </div>
          </div>

          <div className="panel-section">
            <div className="panel-section-title">打印</div>
            <div className="property-item" onClick={() => handleSettingsChange('showPageNumbers', !settings.showPageNumbers)}>
              <span className="property-label">显示页码</span>
              <div className={`toggle-switch ${settings.showPageNumbers ? 'active' : ''}`} />
            </div>
          </div>

        </aside>
      </div>

      {showVocabPicker && (
        <VocabularyPicker
          onSelect={(words, mode = 'replace', meta) => {
            const importedText = words.join(' ');
            const nextText = mode === 'append' && text.trim()
              ? `${text.trim()} ${importedText}`
              : importedText;
            setText(nextText);
            setCustomTitle(buildVocabularyTitle(meta));
            setCurrentPoemLines(null);
            setCurrentPoems([]);
            setCurrentPoemTitleRows([]);
            setSettings(prev => ({
              ...prev,
              ...calculateAutoLayout(nextText, prev),
            }));
            setShowVocabPicker(false);
          }}
          onClose={() => setShowVocabPicker(false)}
        />
      )}

      {showPoemPicker && (
        <AncientPoemPicker
          onSelect={(poems, mode = 'replace') => {
            const selectedPoems = Array.isArray(poems) ? poems : [poems];
            const nextPoems = mode === 'append' && currentPoems.length > 0
              ? [...currentPoems, ...selectedPoems]
              : selectedPoems;
            const worksheet = buildPoemWorksheet(nextPoems, settings.colsPerRow);
            setText(worksheet.text);
            setCustomTitle(buildPoemTitle(nextPoems));
            setCurrentPoemLines(worksheet.rawLines);
            setCurrentPoems(nextPoems);
            setCurrentPoemTitleRows(worksheet.titleRows);
            setSettings(prev => ({
              ...prev,
              ...calculatePoemLayout(prev),
            }));
            setShowPoemPicker(false);
          }}
          onClose={() => setShowPoemPicker(false)}
        />
      )}

      {showLiteracyPicker && (
        <Suspense fallback={null}>
          <LiteracyPicker
            onSelect={(charactersToImport, mode = 'replace', meta) => {
              const importedText = charactersToImport.join('');
              const nextText = mode === 'append' && text.trim()
                ? `${text.trim()}\n${importedText}`
                : importedText;
              setText(nextText);
              setCustomTitle(buildLiteracyTitle(meta));
              setCurrentPoemLines(null);
              setCurrentPoems([]);
              setCurrentPoemTitleRows([]);
              setSettings(prev => ({
                ...prev,
                ...calculateAutoLayout(nextText, prev),
              }));
              setShowLiteracyPicker(false);
            }}
            onClose={() => setShowLiteracyPicker(false)}
          />
        </Suspense>
      )}
    </div>
  );
}

export default App;
